// The approval a task waits on, as its chat shows it (D85): the node's question and options
// from the task's graph, and the step before it with what it reported. A note typed with the
// answer travels encrypted into the task's conversation for the next step.
import type { ApprovalNode } from "../../catalog/types";
import type { TaskDetail, TaskStep } from "../model";

/** The plaintext `type` of a note; the daemon keeps only these (crates/ocs-infrad approval_notes). */
export const APPROVAL_NOTE = "approval.note";
/** The longest note sent (the daemon cuts at 8 000 characters). */
export const MAX_NOTE_CHARS = 8000;

export interface PendingApproval {
  node: string;
  /** The node's name, or its id. */
  title: string;
  question: string;
  options: string[];
  /** The last finished step before the approval, if any. */
  previous: { title: string; outcome: string | null; outputs: [string, string][] } | null;
}

/** The approval the task waits on, or null when it does not wait on one. */
export function pendingApproval(detail: TaskDetail): PendingApproval | null {
  const active = detail.task.active_step;
  if (detail.task.status !== "waiting_for_human" || active?.kind !== "approval") return null;
  const node = detail.definition.nodes.find((n): n is ApprovalNode => n.kind === "approval" && n.id === active.node);
  if (!node) return null;
  const titleOf = (id: string) => detail.definition.nodes.find((n) => n.id === id)?.name ?? id;
  const previous = lastFinished(detail.steps);
  return {
    node: node.id,
    title: node.name ?? node.id,
    question: node.question,
    options: node.options,
    previous: previous
      ? { title: titleOf(previous.node_id), outcome: previous.outcome ?? null, outputs: Object.entries(previous.outputs) }
      : null,
  };
}

function lastFinished(steps: TaskStep[]): TaskStep | undefined {
  return steps
    .filter((s) => s.kind === "prompt" && s.finished_at != null)
    .sort((a, b) => (a.finished_at ?? 0) - (b.finished_at ?? 0))
    .at(-1);
}

/** The encrypted note's plaintext: the daemon keeps it for the step after `node`. */
export function notePlaintext(taskId: string, node: string, text: string): string {
  return JSON.stringify({ type: APPROVAL_NOTE, task_id: taskId, node_id: node, text: text.trim().slice(0, MAX_NOTE_CHARS) });
}

/** A Move back's note (D93): the step it moved back to, then why, so the agent reads it in context. */
export const moveBackNote = (name: string, note: string) => `Moved back to ${name}.\n\n${note.trim()}`;
